import { useState } from 'react';
import type React from 'react';
import { Input } from '@/components/ui/input';
import { parseDollars } from '@/lib/parseDollars';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

/**
 * A dollar text box with commas as you type. A decimal point being typed is
 * held as typed until they leave the box, so the formatting can't wipe it out
 * mid-keystroke; the number handed back is whole dollars (parseDollars),
 * never below 0 unless `allowNegative` (a line v32 lets go negative).
 */
export function DollarInput({ value, onValueChange, allowNegative = false, blankWhenZero = true, onBlur, ...rest }:
  Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> & {
    value: number | undefined;
    onValueChange: (v: number) => void;
    allowNegative?: boolean;
    blankWhenZero?: boolean;
  }) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = value === undefined || (blankWhenZero && !value) ? '' : value.toLocaleString('en-US');
  return (
    <Input
      {...rest}
      type="text"
      inputMode="decimal"
      value={draft ?? shown}
      onChange={e => {
        // Commas appear as they type; only a decimal point in progress (or,
        // on a line that can be a loss, a lone "-") is held as typed, since
        // formatting would erase it.
        const text = e.target.value;
        setDraft(/\.\d*\s*$/.test(text) || (allowNegative && /^\s*-\s*$/.test(text)) ? text : null);
        onValueChange(parseDollars(text, allowNegative));
      }}
      onBlur={e => { setDraft(null); onBlur?.(e); }}
    />
  );
}

/** A wizard money box. Floors at 0 unless `allowNegative` — only the six lines
 *  v32 lets go negative (capital gains, Schedule 1 other income, K-1) get it. */
export function CurrencyField({ label, value, onChange, hint, allowNegative = false }: { label: string; value: number; onChange: (v: number) => void; hint?: string; allowNegative?: boolean }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 text-sm font-mono">$</span>
        <DollarInput
          value={value}
          onValueChange={onChange}
          allowNegative={allowNegative}
          className="pl-7 h-10 text-sm font-mono"
          placeholder="0"
        />
      </div>
      {hint && <p className="text-xs text-muted-foreground italic">{hint}</p>}
    </div>
  );
}

export function ToggleField({ label, value, onChange, disabled, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: string }) {
  return (
    <div className={`flex flex-col rounded-lg border px-4 py-3 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm text-foreground">{label}</Label>
        <Switch checked={value} onCheckedChange={onChange} disabled={disabled} />
      </div>
      {hint && <p className="text-xs text-muted-foreground italic mt-1">{hint}</p>}
    </div>
  );
}

export function NumberField({ label, value, onChange, step, placeholder, suffix, hint }: { label: string; value: number; onChange: (v: number) => void; step?: string; placeholder?: string; suffix?: string; hint?: string }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="relative">
        <Input
          type="number"
          step={step}
          value={value || ''}
          onChange={e => onChange(Number(e.target.value) || 0)}
          className="h-10 text-sm font-mono"
          placeholder={placeholder || '0'}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-muted-foreground italic">{hint}</p>}
    </div>
  );
}
